import type { LeadEvent, LostReason, Source, Stage, TouchKind } from './types';
import { dateOnly, dateTime, inr, lostReasonLabel, outcomeLabel, proofKindLabel, registrationChannelLabel, rupees, sourceLabel, stageLabel, touchLabel } from './format';

// One line of plain English for each timeline event. Unknown kinds are shown by name, never hidden.
export function describeEvent(e: LeadEvent, nameOf: (memberId: string | null | undefined) => string): { title: string; detail?: string } {
  const b = e.body as Record<string, string | boolean | undefined>;
  switch (e.kind) {
    case 'created':
      return { title: `Enquiry captured · ${sourceLabel[b.source as Source] ?? b.source}`, detail: (b.message as string) || (b.source_detail as string) };
    case 'enquiry':
      return { title: `Asked again · ${sourceLabel[b.source as Source] ?? b.source}`, detail: (b.message as string) || (b.source_detail as string) };
    case 'assigned':
      return { title: `Assigned to ${nameOf(b.owner_id as string)}`, detail: b.reason as string };
    case 'reassigned':
      return { title: `Moved from ${nameOf(b.from as string)} to ${nameOf(b.to as string)}`, detail: b.reason as string };
    case 'note':
      return { title: 'Note', detail: b.text as string };
    case 'stage_changed': {
      const to = stageLabel[b.to as Stage] ?? b.to;
      const extra = b.lost_reason ? ` · ${lostReasonLabel[b.lost_reason as LostReason]}` : b.dormant_until ? ` · wakes ${b.dormant_until}` : '';
      return { title: `Stage: ${stageLabel[b.from as Stage] ?? b.from} → ${to}${extra}` };
    }
    case 'next_step_set':
      return { title: `Next step for ${dateTime(b.at as string)}`, detail: b.note as string };
    case 'touch':
      return { title: `${touchLabel[b.kind as TouchKind] ?? b.kind} logged`, detail: b.note as string };
    case 'handoff':
      return { title: 'Needs a person', detail: b.reason as string };
    case 'ai_update': {
      const parts = [b.unit_type && `Unit: ${b.unit_type}`, b.budget_max_inr && `Budget up to ₹${Number(b.budget_max_inr).toLocaleString('en-IN')}`,
        b.horizon && `Timeline: ${b.horizon}`, b.score && `Score: ${b.score}${b.score_reason ? ` (${b.score_reason})` : ''}`].filter(Boolean);
      return { title: 'AI desk updated the lead', detail: parts.join(' · ') };
    }
    case 'ai_reply_approved':
      return { title: b.edited ? 'AI reply edited and approved' : 'AI reply approved' };
    case 'ai_reply_rejected':
      return { title: 'AI reply rejected', detail: b.note as string };
    case 'time_limit_missed':
      return { title: `${nameOf(b.owner_id as string)} missed the response time limit`, detail: Number(b.missed_count) >= 2 ? 'Second miss in a row' : undefined };
    case 'visit_booked':
      return { title: `Site visit booked for ${dateTime(b.at as string)}`, detail: [b.project, b.note].filter(Boolean).join(' · ') };
    case 'visit_done':
      return { title: `Site visit done · ${outcomeLabel[b.outcome as string] ?? b.outcome}`, detail: [b.note, b.next_note && `Next: ${b.next_note}`].filter(Boolean).join(' · ') };
    case 'visit_no_show':
      return { title: 'Buyer did not come for the visit', detail: b.next_note ? `Next: ${b.next_note}` : undefined };
    case 'visit_cancelled':
      return { title: 'Site visit cancelled', detail: b.note as string };
    case 'call_details': {
      const parts = [b.unit_type && `Unit: ${b.unit_type}`, b.budget_max_inr && `Budget up to ${inr(Number(b.budget_max_inr))}`, b.horizon && `Timeline: ${b.horizon}`,
        b.next_note && `Next: ${b.next_note}`, b.to && `Stage → ${stageLabel[b.to as Stage]}`].filter(Boolean);
      return { title: 'Details updated from the call', detail: parts.join(' · ') };
    }
    case 'registration_sent':
      return { title: `Registered with the builder (${registrationChannelLabel[b.via as string] ?? b.via})`, detail: [b.to && `To ${b.to}`, b.valid_until && `Valid until ${dateOnly(b.valid_until as string)}`].filter(Boolean).join(' · ') };
    case 'registration_acknowledged':
      return { title: 'Builder acknowledged the registration', detail: [b.builder_ref && `Ref ${b.builder_ref}`, b.valid_until && `Valid until ${dateOnly(b.valid_until as string)}`].filter(Boolean).join(' · ') };
    case 'registration_rejected':
      return { title: 'Builder rejected the registration', detail: b.reason as string };
    case 'registration_withdrawn':
      return { title: 'Registration withdrawn', detail: b.reason as string };
    case 'proof_added':
      return { title: `Proof added · ${proofKindLabel[b.kind as string] ?? b.kind}`, detail: b.note as string };
    case 'booking_recorded':
      return { title: `Booking recorded${b.unit ? ` · ${b.unit}` : ''}`, detail: `Agreement value ${rupees(Number(b.agreement_value_inr))} · commission ${b.commission_pct}%` };
    case 'commission_received':
      return { title: `Commission received · ${rupees(Number(b.amount_inr))}`, detail: [b.milestone, b.tds_inr && `TDS ${rupees(Number(b.tds_inr))}`].filter(Boolean).join(' · ') };
    case 'booking_cancelled':
      return { title: 'Booking cancelled', detail: b.reason as string };
    case 'woke':
      return { title: 'Woke up from dormant', detail: b.reason as string };
    default:
      return { title: e.kind.replace(/_/g, ' ') };
  }
}

export function actorName(e: LeadEvent, nameOf: (id: string | null | undefined) => string): string {
  if (e.actor_kind === 'member') return nameOf(e.actor_member_id);
  if (e.actor_kind === 'ai') return 'AI desk';
  if (e.actor_kind === 'buyer') return 'Buyer';
  return 'ReAstra';
}
